<template>
  <div class="occasion-step">
    <div class="occasion-content">
      <!-- Title -->
      <div class="title-section">
        <h2 class="step-title">{{ step.title }}</h2>
      </div>

      <!-- Radio Buttons -->
      <div class="form-fields">
        <div class="field-group">
          <div class="radio-group">
            <div
              v-for="occasion in step.occasions"
              :key="occasion.value"
              class="radio-option"
            >
              <RadioButton
                :inputId="`occasion-${occasion.value}`"
                v-model="selectedOccasion"
                :value="occasion.value"
                @update:model-value="updateOccasion"
              />
              <label :for="`occasion-${occasion.value}`" class="radio-label">
                {{ occasion.label }}
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import RadioButton from 'primevue/radiobutton'
import { useLookModal } from '@/features/look/store'
import type { LookStep } from '@/features/look/types/look-config'

interface Props {
  step: LookStep & { type: 'occasion' }
}

const props = defineProps<Props>()
const lookStore = useLookModal()

const selectedOccasion = ref<string | null>(null)

function updateOccasion() {
  lookStore.updateLookData(props.step.id, selectedOccasion.value)
}

onMounted(() => {
  const data = lookStore.lookModal.lookData
  const savedOccasion = data[props.step.id]
  selectedOccasion.value = savedOccasion || null
})
</script>

<style scoped>
.occasion-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
}

.occasion-content {
  max-width: 500px;
  width: 100%;
}

.title-section {
  margin-bottom: 32px;
  text-align: left;
}

.step-title {
  font-size: 24px;
  font-weight: 600;
  color: #1d1d1f;
  margin: 0 0 12px 0;
}

.step-subtitle {
  font-size: 16px;
  color: #666;
  line-height: 1.5;
  margin: 0;
}

.form-fields {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.radio-group {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.radio-option {
  display: flex;
  align-items: center;
  gap: 12px;
}

.radio-label {
  font-size: 16px;
  color: #1d1d1f;
  cursor: pointer;
}


@media (min-width: 1024px) {
  .occasion-step {
    padding: 0;
  }

  .occasion-content {
    padding: 40px 60px;
    gap: 40px;
    max-width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .title-section {
    margin-bottom: 0;
    text-align: center;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 8px;
    justify-content: center;
    align-items: center;
  }

  .step-title {
    font-family: 'Gilroy', sans-serif;
    font-size: 48px;
    font-weight: 700;
    line-height: 1.2;
    letter-spacing: -0.04em;
    color: #232F45;
    text-align: center;
    margin: 0;
  }

  .step-subtitle {
    font-family: 'Inter', sans-serif;
    font-size: 20px;
    font-weight: 400;
    line-height: 1.3;
    letter-spacing: -0.04em;
    color: rgba(35, 47, 69, 0.8);
  }

  .form-fields {
    width: 100%;
    gap: 0;
  }

  .field-group {
    width: 100%;
  }

  .radio-group {
    gap: 8px;
    width: 100%;
  }

  .radio-option {
    padding: 12px 20px;
    gap: 8px;
    border-radius: 8px;
    align-self: stretch;
  }

  .radio-label {
    font-family: 'Inter', sans-serif;
    font-size: 16px;
    font-weight: 400;
    line-height: 1.3;
    letter-spacing: -0.04em;
    color: #232F45;
  }
}


@media (min-width: 1919px) {
  .occasion-content {
    padding: 40px 120px;
  }
}
</style>

